import { useCallback } from 'react';
import { Search, Users, Filter } from 'lucide-react';
import { useAgentStore, useFilteredAgents } from '../../stores/agentStore';
import { usePolling } from '../../hooks/usePolling';
import { AGENTS, DEPARTMENTS } from '../../utils/agents';
import { EmptyState } from '../common/EmptyState';
import FeaturedAgent from './FeaturedAgent';
import AgentCard from './AgentCard';
import AgentDetail from './AgentDetail';

const POLL_INTERVAL = 60_000;

const CSUITE_OPTIONS = [
  { slug: 'ceo-agent', label: 'CEO' },
  { slug: 'cfo-agent', label: 'CFO' },
  { slug: 'cmo-agent', label: 'CMO' },
  { slug: 'coo-agent', label: 'COO' },
  { slug: 'cco-agent', label: 'CCO' },
];

// Map department ID to the department key used in agent data
const DEPT_KEY_MAP: Record<string, string> = {
  '00': '00-Admin',
  '01': '01-Brand',
  '02': '02-Products',
  '03': '03-Campaigns',
  '04': '04-Content',
  '05': '05-Email',
  '06': '06-Socials',
  '07': '07-Ads',
  '08': '08-Website',
  '09': '09-UGC',
  '10': '10-Marketplaces',
  '11': '11-Analytics',
  '12': '12-Finance',
  '13': '13-Codebases',
};

export default function AgentsPage() {
  const fetchAgents = useAgentStore(s => s.fetchAgents);
  const fetchAgentDetail = useAgentStore(s => s.fetchAgentDetail);
  const selectedAgent = useAgentStore(s => s.selectedAgent);
  const setSelectedAgent = useAgentStore(s => s.setSelectedAgent);
  const filter = useAgentStore(s => s.filter);
  const setFilter = useAgentStore(s => s.setFilter);
  const filteredAgents = useFilteredAgents();

  usePolling(fetchAgents, POLL_INTERVAL);

  const handleSelectAgent = useCallback(
    (slug: string) => {
      fetchAgentDetail(slug);
    },
    [fetchAgentDetail]
  );

  const handleCloseDetail = useCallback(() => {
    setSelectedAgent(null);
  }, [setSelectedAgent]);

  const handleNavigate = useCallback(
    (slug: string) => {
      fetchAgentDetail(slug);
    },
    [fetchAgentDetail]
  );

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      {/* Featured Agent */}
      <FeaturedAgent onSelect={handleSelectAgent} />

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* C-Suite Filter */}
        <div className="flex items-center gap-1.5">
          <Filter className="w-4 h-4 text-text-muted" />
          <select
            value={filter.csuite || ''}
            onChange={(e) => setFilter({ csuite: e.target.value || null })}
            className="bg-bg-tertiary border border-border-subtle rounded px-2.5 py-1.5 text-sm text-text-primary focus:outline-none focus:border-accent-purple appearance-none cursor-pointer min-w-[100px]"
          >
            <option value="">All C-Suite</option>
            {CSUITE_OPTIONS.map(opt => (
              <option key={opt.slug} value={opt.slug}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Department Filter */}
        <select
          value={filter.department || ''}
          onChange={(e) => setFilter({ department: e.target.value || null })}
          className="bg-bg-tertiary border border-border-subtle rounded px-2.5 py-1.5 text-sm text-text-primary focus:outline-none focus:border-accent-purple appearance-none cursor-pointer min-w-[160px]"
        >
          <option value="">All Departments</option>
          <option value="C-Suite">C-Suite</option>
          {DEPARTMENTS.map(dept => (
            <option key={dept.id} value={DEPT_KEY_MAP[dept.id]}>
              {dept.id} - {dept.name}
            </option>
          ))}
        </select>

        {/* Search */}
        <div className="relative flex-1 min-w-[140px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search agents..."
            value={filter.search}
            onChange={(e) => setFilter({ search: e.target.value })}
            className="w-full bg-bg-tertiary border border-border-subtle rounded pl-8 pr-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-purple"
          />
        </div>

        {/* Agent count */}
        <span className="text-xs text-text-muted ml-auto">
          {filteredAgents.length} of {AGENTS.length} agents
        </span>
      </div>

      {/* Agent Grid */}
      {filteredAgents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredAgents.map(agent => (
            <AgentCard
              key={agent.slug}
              agent={agent}
              onClick={() => handleSelectAgent(agent.slug)}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Users />}
          title="No Agents Found"
          description="Try adjusting your filters or search query."
        />
      )}

      {/* Detail Panel */}
      {selectedAgent ? (
        <AgentDetail
          agent={selectedAgent}
          onClose={handleCloseDetail}
          onNavigate={handleNavigate}
        />
      ) : null}
    </div>
  );
}
